import { useCallback, useEffect, useMemo, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useToast } from '../../../../../hooks/useToast';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useInvoiceAutopay } from '../useInvoiceAutopay';
import { useBlockingRouter } from '../../../../../hooks/useBlockingRouter';
import {
	UseInvoiceAutoPayUpdateInvoiceFragment,
	PaymentMethodType,
} from '#types';
import { gql } from '@apollo/client';
import { getPaymentMethodOrder } from '../../../../../mui/MuiPaymentMethod/helpers';

export const useInvoiceAutoPayUpdateInvoiceFragment = gql`
	fragment useInvoiceAutoPayUpdateInvoice on Invoice {
		id
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
			}
		}
		organization {
			id
			country
		}
	}
`;

export type UseInvoiceAutopayUpdateProps = {
	invoiceId: string;
	invoice?: UseInvoiceAutoPayUpdateInvoiceFragment;
	guestToken?: string;
	stripeElementOptions: StripeElementsOptions;
} & Pick<ReturnType<typeof useInvoiceAutopay>, 'onActivateOrUpdate'>;

export const useInvoiceAutoPayUpdate = ( {
	invoiceId,
	invoice,
	guestToken,
	stripeElementOptions,
	onActivateOrUpdate,
}: UseInvoiceAutopayUpdateProps ) => {
	const router = useBlockingRouter();
	const stripe = useStripe();
	const elements = useElements();
	const { showError } = useToast();
	const [ loading, setLoading ] = useState( false );
	const [ paymentMethodType, setPaymentMethodType ] = useState<
		PaymentMethodType | undefined
	>( undefined );
	const [ paymentMethodOrder, setPaymentMethodOrder ] = useState<
		PaymentMethodType[] | undefined
	>();

	useEffect( () => {
		if ( paymentMethodType ) {
			setPaymentMethodOrder( ( prevState ) =>
				getPaymentMethodOrder( prevState, paymentMethodType )
			);
		}
	}, [ paymentMethodType ] );

	const billing_details = useMemo( () => {
		return {
			email: invoice?.contact?.customer?.email,
			name: `${ invoice?.contact?.firstName } ${ invoice?.contact?.lastName }`,
		};
	}, [ invoice ] );

	const onSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
		async ( e: React.FormEvent ) => {
			e.preventDefault();
			setLoading( true );
			const return_url = window.location.href;
			if ( stripeElementOptions && stripe && elements ) {
				const { error: elementsError } = await elements.submit();
				if ( elementsError ) {
					setLoading( false );
					showError( elementsError.message );
				} else {
					const { error: stripeError, setupIntent } = await stripe.confirmSetup(
						{
							elements,
							confirmParams: {
								return_url,
								payment_method_data: {
									allow_redisplay: guestToken ? 'limited' : 'always',
									billing_details,
								},
							},
							redirect: 'if_required',
							...stripeElementOptions,
						}
					);
					const paymentMethod = setupIntent?.payment_method
						? typeof setupIntent.payment_method === 'string'
							? setupIntent.payment_method
							: setupIntent.payment_method?.id
						: undefined;
					if ( stripeError ) {
						setLoading( false );
						showError( stripeError.message );
					} else if ( !setupIntent ) {
						// If there's no setupIntent and no error then we're redirecting, which is OK.
					} else if ( paymentMethod ) {
						// in the case of no redirect we end up here
						setLoading( false );
						await onActivateOrUpdate( paymentMethod );
					} else {
						// if we end up here, something went wrong that we don't know how to handle
						setLoading( false );
						showError( 'Failed to update autopay settings' );
					}
				}
			}
		},
		[
			stripeElementOptions,
			stripe,
			elements,
			showError,
			guestToken,
			onActivateOrUpdate,
			billing_details,
		]
	);

	// when we come back from the stripe redirect we can finally update the autopay settings
	const clientSecret = new URLSearchParams( window.location.search ).get(
		'setup_intent_client_secret'
	);
	useEffect( () => {
		const handleSetupIntent = async () => {
			if ( stripeElementOptions && stripe && elements && clientSecret ) {
				const result = await stripe.retrieveSetupIntent( clientSecret );
				const paymentMethod = result.setupIntent?.payment_method
					? typeof result.setupIntent.payment_method === 'string'
						? result.setupIntent.payment_method
						: result.setupIntent.payment_method.id
					: undefined;
				// remove the URL params from the redirect in a way that disables using the "back button" to get back to those params
				await router.replace(
					`/invoice/${ invoiceId }${ guestToken ? `?token=${ guestToken }` : '' }`
				);
				if ( paymentMethod ) {
					await onActivateOrUpdate( paymentMethod );
				} else {
					showError( 'Failed to update autopay settings' );
				}
			}
		};
		handleSetupIntent();
	}, [
		stripeElementOptions,
		stripe,
		elements,
		showError,
		onActivateOrUpdate,
		router,
		invoiceId,
		guestToken,
		clientSecret,
	] );

	return {
		onSubmit,
		loading,
		paymentMethodType,
		setPaymentMethodType,
		paymentMethodOrder,
		billing_details,
	};
};
